/**
 * A no-show's grounds, in the words the office reads them in.
 *
 * ONE VOCABULARY, TWO SCREENS. A contested no-show is looked at in two
 * places — the booking's Driver events tab on the dashboard (where the
 * photos are, and where the claim is settled) and the dispatcher's trip
 * screen (where it is first noticed) — and they must not describe the same
 * claim in two different ways. Both read this.
 *
 * The rows are deliberately blunt about what is NOT proven. "Position not
 * confirmed" is a row of its own rather than an omission, because roughly
 * 7% of driver events carry no fix and a missing line reads as "fine" to
 * someone scanning. The same for a landing we could not read: the office
 * has to know the 60 minutes were never counted, only agreed.
 */

/** The three steps of the office's own instruction, as the driver ticked them. */
export const NO_SHOW_CHECKPOINT_LABELS = {
  stayed_at_meeting_point: 'Stayed at the agreed meeting point',
  checked_landing_time: 'Checked the actual landing time',
  called_passenger: 'Called the passenger from the app',
};

/**
 * WHICH CLOCK LET THE REPORT THROUGH. The single most important line for a
 * dispute: either the 60 minutes were counted from a real touchdown, or
 * they were never counted at all and a person agreed to it.
 */
export const NO_SHOW_BASIS_LABELS = {
  actual_landing: '60 minutes from the actual landing',
  unknown: 'Flight status unavailable — agreed with the office instead',
  not_airport: 'Not an airport pickup — agreed with the office',
};

export const NO_SHOW_POSITION_MISSING = 'Position not confirmed — the phone gave no fix';

/**
 * WAS THE DRIVER VISIBLY THERE? The fact a partner disputes hardest, and
 * the one the app can answer best when the sign was ours: we rendered it,
 * so what it displayed is known rather than photographed.
 */
export const NAME_SIGN_LABELS = {
  app: 'Held the app\'s name sign',
  own: 'Held their own name sign',
  none: 'No name sign',
};

/**
 * One line about the sign, or '' when the driver was never asked — which
 * must not read as "no sign". Takes the booking's own record
 * (`trip.name_sign`), not a copy inside a claim: the answer is given long
 * before any no-show and belongs to trips that never produce one.
 */
export const noShowNameSignText = (nameSign) => {
  const label = NAME_SIGN_LABELS[nameSign?.kind];
  if (!label) return '';

  return nameSign.displayed ? `${label}, reading "${nameSign.displayed}"` : label;
};

/** A checkpoint the driver did NOT confirm still has to be visible. */
export const noShowCheckpointRows = (evidence) => {
  const confirmed = new Set(evidence?.checkpoints || []);

  return Object.entries(NO_SHOW_CHECKPOINT_LABELS).map(([key, label]) => ({
    key,
    label,
    confirmed: confirmed.has(key),
  }));
};

/** `{ lat, lon }` from the stored evidence, or null when none was recorded. */
export const noShowPoint = (evidence) => {
  // The portal view sends `position`; the terminal-log copy carries the raw
  // `lat` / `lon` strings it was written with. Both mean the same thing.
  const lat = Number(evidence?.position?.lat ?? evidence?.lat);
  const lon = Number(evidence?.position?.lon ?? evidence?.lon);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;

  return { lat, lon };
};

/**
 * How the wait is summarised in one line: the minutes actually waited and
 * from when, or plainly that no landing time was ever read.
 */
export const noShowWaitText = (evidence) => {
  const basis = evidence?.landing_basis || 'unknown';
  const label = NO_SHOW_BASIS_LABELS[basis] || NO_SHOW_BASIS_LABELS.unknown;

  if (basis !== 'actual_landing') return label;

  const minutes = Number(evidence?.waited_minutes) || 0;

  return `${label} — waited ${minutes} min`;
};

/** What each photo was filed as. An old one with no category says so. */
export const NO_SHOW_PHOTO_LABELS = {
  meeting_point: 'Meeting point',
  conversation: 'Chat / call log',
  name_sign: 'Name sign',
  other: 'Other evidence',
};

export const noShowPhotoLabel = (category) => NO_SHOW_PHOTO_LABELS[category] || 'Uncategorised';

const CONTACT_METHOD_LABELS = { call: 'called', whatsapp: 'messaged on WhatsApp' };

/**
 * How many attempts to reach the passenger were on file, and by which
 * routes — "rang them" and "messaged them" are different facts in a
 * dispute, so the count alone will not do.
 */
export const noShowCallText = (evidence) => {
  const attempts = Number(evidence?.call_attempts) || 0;
  const noun = attempts === 1 ? '1 attempt to reach the passenger' : `${attempts} attempts to reach the passenger`;
  const methods = (evidence?.contact_methods || [])
    .map((method) => CONTACT_METHOD_LABELS[method])
    .filter(Boolean);

  return methods.length ? `${noun} (${methods.join(', ')})` : noun;
};
